import { type RefObject, useCallback, useEffect, useRef } from 'react';

import { useTrack } from 'src/contexts/AnalyticsContext';
import useIntersection from 'src/hooks/useIntersection/useIntersection';
import { Events } from 'src/lib/trackingEvents';

/**
 * The Segment events for the LendScore calculator. Every event carries the
 * entry's `trackingContext`, which is how an editor tells two calculators
 * apart in Segment.
 *
 * - VIEWED: the calculator first scrolls into view.
 * - CLICKED: the button on the result card is clicked.
 * - ENGAGED: the user changes one of the inputs for the first time.
 * - ABANDONED: the user engaged, then left without clicking the button.
 */
export const useLendScoreTracking = ({
  inputs,
  sectionRef,
  trackingContext,
}: {
  // What the user has chosen so far, sent along with the events that say
  // what they did with it. A rate the ROI data has no figure for is
  // undefined.
  inputs: Record<string, number | string | undefined>;
  sectionRef: RefObject<HTMLElement>;
  trackingContext?: Record<string, unknown>;
}) => {
  const track = useTrack();
  const isInView = useIntersection({
    element: sectionRef,
    threshold: 0.01,
    useOnce: true,
  });
  const hasViewed = useRef(false);
  const hasEngaged = useRef(false);
  const hasClicked = useRef(false);
  const hasAbandoned = useRef(false);

  // `track` changes identity once ad block detection resolves, and the
  // inputs and context change as the user works, so the events read them when
  // they fire rather than closing over them. That keeps the one-time events
  // from firing again and the rest from reporting stale values.
  const latest = useRef({ inputs, track, trackingContext });
  useEffect(() => {
    latest.current = { inputs, track, trackingContext };
  });

  useEffect(() => {
    if (!isInView || hasViewed.current) return;

    hasViewed.current = true;
    latest.current.track({
      type: Events.VIEWED,
      payload: { ...latest.current.trackingContext },
    });
  }, [isInView]);

  const trackCtaClick = useCallback(() => {
    // Once they have clicked through, leaving is the point rather than a
    // lost visit.
    hasClicked.current = true;

    const {
      inputs: current,
      track: send,
      trackingContext: context,
    } = latest.current;

    send({
      type: Events.CLICKED,
      payload: { ...context, ...current, control: 'cta' },
    });
  }, []);

  // Called as an input changes, with the input and the value it changed to.
  // Only the first change is an event, since it is the start of using the
  // calculator that matters. The inputs aren't sent because they haven't
  // caught up with the change yet.
  const trackEngaged = useCallback(
    (control: string, value: number | string) => {
      if (hasEngaged.current) return;

      hasEngaged.current = true;
      latest.current.track({
        type: Events.ENGAGED,
        payload: { ...latest.current.trackingContext, control, value },
      });
    },
    [],
  );

  useEffect(() => {
    const trackAbandoned = () => {
      if (!hasEngaged.current || hasClicked.current || hasAbandoned.current) {
        return;
      }

      hasAbandoned.current = true;

      const {
        inputs: current,
        track: send,
        trackingContext: context,
      } = latest.current;

      send({ type: Events.ABANDONED, payload: { ...context, ...current } });
    };

    // A page that is closed or switched away from may never be seen again,
    // and on mobile `visibilitychange` is the last event that is reliably
    // delivered. `pagehide` covers browsers that skip it on a close.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') trackAbandoned();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', trackAbandoned);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', trackAbandoned);

      // Moving to another page of the site unmounts the calculator without
      // the page ever being hidden.
      trackAbandoned();
    };
  }, []);

  return { trackCtaClick, trackEngaged };
};
